export type MetricFilterOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';

export type MetricFilter = {
  id: string;
  metric: string;
  operator: MetricFilterOperator;
  value: number | '';
};

export type CompleteMetricFilter = MetricFilter & { value: number };

export type MetricFilterPayload = {
  metric: string;
  operator: MetricFilterOperator;
  value: number;
};

export type SavedMetricFilterPreset = {
  id: string;
  name: string;
  filters: MetricFilter[];
};

export type MetricFilterOption = {
  key: string;
  label: string;
};

export const METRIC_FILTER_OPERATORS: Array<{
  value: MetricFilterOperator;
  label: string;
  symbol: string;
}> = [
  { value: 'gt', label: 'Greater than', symbol: '>' },
  { value: 'gte', label: 'Greater than or equal', symbol: '>=' },
  { value: 'lt', label: 'Less than', symbol: '<' },
  { value: 'lte', label: 'Less than or equal', symbol: '<=' },
  { value: 'eq', label: 'Equal to', symbol: '=' },
  { value: 'neq', label: 'Not equal to', symbol: '!=' },
];

const OPERATOR_BY_VALUE = new Map(METRIC_FILTER_OPERATORS.map((item) => [item.value, item]));

export function isCompleteMetricFilter(filter: MetricFilter): filter is CompleteMetricFilter {
  return typeof filter.value === 'number' && Number.isFinite(filter.value);
}

export function completeMetricFilters(filters: MetricFilter[]): CompleteMetricFilter[] {
  return filters.filter(isCompleteMetricFilter);
}

export function createEmptyMetricFilter(defaultMetric = 'impressions'): MetricFilter {
  return {
    id: crypto.randomUUID(),
    metric: defaultMetric,
    operator: 'gt',
    value: '',
  };
}

export function cloneMetricFilters(filters: MetricFilter[]): MetricFilter[] {
  return filters.map((filter) => ({ ...filter, id: crypto.randomUUID() }));
}

export function metricValue(metrics: Record<string, unknown> | null | undefined, key: string): number {
  const raw = metrics?.[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
}

export function compareMetricValue(
  cellValue: number,
  operator: MetricFilterOperator,
  threshold: number
): boolean {
  switch (operator) {
    case 'gt':
      return cellValue > threshold;
    case 'gte':
      return cellValue >= threshold;
    case 'lt':
      return cellValue < threshold;
    case 'lte':
      return cellValue <= threshold;
    case 'eq':
      return cellValue === threshold;
    case 'neq':
      return cellValue !== threshold;
    default:
      return false;
  }
}

/** True when metrics pass every complete filter (AND). Incomplete filters are ignored. */
export function rowMatchesMetricFilters(
  metrics: Record<string, unknown> | null | undefined,
  filters: MetricFilter[]
): boolean {
  const complete = completeMetricFilters(filters);
  if (complete.length === 0) return true;
  for (const filter of complete) {
    if (!compareMetricValue(metricValue(metrics, filter.metric), filter.operator, filter.value)) {
      return false;
    }
  }
  return true;
}

export function toMetricFilterPayloads(filters: MetricFilter[]): MetricFilterPayload[] {
  return completeMetricFilters(filters).map(({ metric, operator, value }) => ({
    metric,
    operator,
    value,
  }));
}

export function serializeMetricFilters(filters: MetricFilter[]): string {
  return JSON.stringify(toMetricFilterPayloads(filters));
}

export function operatorSymbol(operator: MetricFilterOperator): string {
  return OPERATOR_BY_VALUE.get(operator)?.symbol ?? operator;
}

export function defaultSavedFilterName(
  filters: MetricFilter[],
  metrics: readonly MetricFilterOption[]
): string {
  const complete = completeMetricFilters(filters);
  if (complete.length === 0) return 'Saved filter';
  const first = complete[0];
  const label = metrics.find((item) => item.key === first.metric)?.label ?? first.metric;
  const base = `${label} ${operatorSymbol(first.operator)} ${first.value}`;
  if (complete.length === 1) return base;
  return `${base} + ${complete.length - 1} more`;
}

export function parseMetricInput(raw: string): number | '' {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') return '';
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : '';
}

const STORAGE_VERSION = 1;

type StoredPresets = {
  v: number;
  presets: SavedMetricFilterPreset[];
};

export function metricFilterStorageKey(profileId: string, tableId: string): string {
  return `workspace-metric-filters:${profileId}:${tableId}`;
}

export function loadSavedMetricFilters(profileId: string, tableId: string): SavedMetricFilterPreset[] {
  if (typeof window === 'undefined' || !profileId || !tableId) return [];
  try {
    const raw = window.localStorage.getItem(metricFilterStorageKey(profileId, tableId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredPresets;
    if (parsed.v !== STORAGE_VERSION || !Array.isArray(parsed.presets)) return [];
    return parsed.presets.filter(
      (preset) =>
        preset &&
        typeof preset.id === 'string' &&
        typeof preset.name === 'string' &&
        Array.isArray(preset.filters)
    );
  } catch {
    return [];
  }
}

export function saveSavedMetricFilters(
  profileId: string,
  tableId: string,
  presets: SavedMetricFilterPreset[]
): void {
  if (typeof window === 'undefined' || !profileId || !tableId) return;
  const payload: StoredPresets = { v: STORAGE_VERSION, presets };
  window.localStorage.setItem(metricFilterStorageKey(profileId, tableId), JSON.stringify(payload));
}
